/**
 * Background cleanup logic for expired offline editor databases.
 *
 * This module handles checking all databases from other documents and taking action:
 * - Deletes fully-expired databases
 * - Clears expired data while preserving fresh data
 * - Migrates pre-migration databases by adding timestamps
 */

import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance';

import { OFFLINE_STEPS_DATABASE_VERSION } from './stepsDatabase';
import { OFFLINE_STEPS_DATABASE_NAME_PREFIX, OFFLINE_META_STORE_NAME } from './database-schema';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const BACKGROUND_CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000;
const LAST_BACKGROUND_CLEANUP_ATTEMPT_KEY = 'editor:offline:last-background-cleanup-attempt';
const MAX_DATABASES_PER_CLEANUP = 100; // Cap to prevent performance issues on first load

// TODO: Consider reusing StepsDBSchema['meta']['value'] after updating schema to reflect nullable doc/version
interface VersionInsertionBackup {
	adfContent: string;
}

interface MetaData {
	doc: JSONDocNode | null;
	version: number | null;
	lastUpdated?: number;
	versionInsertionBackup?: VersionInsertionBackup;
}

export interface DatabaseCheckResult {
	dbName: string;
	deleted: boolean; // True if database was deleted
	reason:
		| 'empty' // Database had no data
		| 'expired' // Database expired and was deleted
		| 'migrated' // Added timestamp to pre-migration data
		| 'active' // Not expired, no action taken
		| 'error'; // Error checking database
}

/**
 * Check if enough time has passed since the last background cleanup attempt.
 * Uses storage manager to track the last attempt timestamp.
 */
export function shouldRunBackgroundCleanup(): boolean {
	try {
		if (!confluenceLocalStorageInstance) {
			return false;
		}

		const lastAttempt = confluenceLocalStorageInstance.getItem(LAST_BACKGROUND_CLEANUP_ATTEMPT_KEY);

		if (!lastAttempt) {
			return true;
		}

		const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt, 10);

		return timeSinceLastAttempt > BACKGROUND_CLEANUP_INTERVAL_MS;
	} catch (error) {
		// TODO: Add analytics for storage access failure
		return false;
	}
}

/**
 * Mark that a background cleanup attempt is starting.
 * Called immediately before starting cleanup to prevent race condition between tabs.
 * Acts as a distributed lock via localStorage - if multiple tabs open simultaneously,
 * only the first will proceed with cleanup.
 */
export function markBackgroundCleanupAttempted(): void {
	try {
		if (!confluenceLocalStorageInstance) {
			return;
		}

		confluenceLocalStorageInstance.setItem(
			LAST_BACKGROUND_CLEANUP_ATTEMPT_KEY,
			Date.now().toString(),
		);
	} catch (error) {
		// TODO: Add analytics for storage access failure
	}
}

/**
 * Delete an entire database.
 */
async function deleteDatabase(dbName: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.deleteDatabase(dbName);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		request.onblocked = () => resolve();
	});
}

/**
 * Add timestamp to pre-migration data to migrate it.
 */
async function addTimestampToMigrateData(
	database: IDBDatabase,
	metaData?: MetaData,
): Promise<void> {
	const writeTransaction = database.transaction([OFFLINE_META_STORE_NAME], 'readwrite');
	const metaStore = writeTransaction.objectStore(OFFLINE_META_STORE_NAME);

	const updatedMetaData: MetaData = {
		...(metaData || { doc: null, version: null }),
		lastUpdated: Date.now(),
	};

	metaStore.put(updatedMetaData, 1);

	await new Promise<void>((resolve, reject) => {
		writeTransaction.oncomplete = () => resolve();
		writeTransaction.onerror = () => reject(writeTransaction.error);
	});
}

/**
 * Check if a database has expired and delete it if so.
 *
 * Actions:
 * - Empty (no metadata and no steps) → delete
 * - Expired (lastUpdated > 30 days) → delete
 * - Missing metadata or timestamp → add timestamp (migrate)
 * - Active (lastUpdated ≤ 30 days) → keep
 */
export async function checkDatabaseExpiry(dbName: string): Promise<DatabaseCheckResult> {
	return new Promise((resolve) => {
		const request = indexedDB.open(dbName, OFFLINE_STEPS_DATABASE_VERSION);

		request.onerror = () => {
			// Can't open the database - treat as error case
			resolve({
				dbName,
				deleted: false,
				reason: 'error',
			});
		};

		request.onsuccess = async (event) => {
			const database = (event.target as IDBOpenDBRequest).result;

			try {
				const readTransaction = database.transaction(
					[OFFLINE_META_STORE_NAME, 'steps'],
					'readonly',
				);
				const metaStore = readTransaction.objectStore(OFFLINE_META_STORE_NAME);
				const stepsStore = readTransaction.objectStore('steps');

				const metaRequest = metaStore.get(1);
				const metaData = (await new Promise<MetaData | undefined>((resolve, reject) => {
					metaRequest.onsuccess = () => resolve(metaRequest.result);
					metaRequest.onerror = () => reject(metaRequest.error);
				})) as MetaData | undefined;

				const stepsCountRequest = stepsStore.count();
				const stepsCount = await new Promise<number>((resolve, reject) => {
					stepsCountRequest.onsuccess = () => resolve(stepsCountRequest.result);
					stepsCountRequest.onerror = () => reject(stepsCountRequest.error);
				});

				// Empty database: No meta data AND no steps. Safe to delete.
				if (!metaData && stepsCount === 0) {
					await deleteDatabase(dbName);

					resolve({
						dbName,
						deleted: true,
						reason: 'empty',
					});
					return;
				}

				// Migration: Missing metadata or missing timestamp. Add timestamp and skip deletion.
				if (!metaData || !metaData.lastUpdated) {
					await addTimestampToMigrateData(database, metaData);

					resolve({
						dbName,
						deleted: false,
						reason: 'migrated',
					});
					return;
				}

				// At this point, metaData.lastUpdated is guaranteed to exist (migrated above)
				const currentTimestamp = Date.now();
				const databaseAge = currentTimestamp - metaData.lastUpdated!;
				const isExpired = databaseAge > THIRTY_DAYS_MS;

				// Expired: Delete entire database.
				if (isExpired) {
					await deleteDatabase(dbName);

					resolve({
						dbName,
						deleted: true,
						reason: 'expired',
					});
					return;
				}

				// Not expired: Database is active.
				resolve({
					dbName,
					deleted: false,
					reason: 'active',
				});
			} catch (error) {
				// TODO: Add analytics for database check failure
				resolve({
					dbName,
					deleted: false,
					reason: 'error',
				});
			} finally {
				database.close();
			}
		};
	});
}

/**
 * Run background cleanup of all offline editor databases.
 *
 * Checks up to 100 databases (randomised order) and deletes expired ones.
 * Excludes the current document's database.
 *
 * @param currentDbName - The name of the current database to exclude from cleanup
 */
export async function runBackgroundCleanup(currentDbName?: string): Promise<void> {
	try {
		if (!window.indexedDB.databases) {
			return;
		}

		const allDatabases = await window.indexedDB.databases();
		const offlineEditorDatabases = allDatabases.filter(
			(dbInfo) =>
				dbInfo.name?.startsWith(OFFLINE_STEPS_DATABASE_NAME_PREFIX) &&
				dbInfo.name !== currentDbName,
		);

		if (offlineEditorDatabases.length === 0) {
			return;
		}

		// We randomise the order to ensure all databases eventually get checked. Otherwise, we'd check the first N databases every time.
		// We also cap the number of databases checked per run to prevent performance issues. For power users with large backlogs, cleanup will happen gradually over multiple days.
		const randomisedDatabases = offlineEditorDatabases.sort(() => Math.random() - 0.5);
		const databasesToCheck = randomisedDatabases.slice(0, MAX_DATABASES_PER_CLEANUP);

		for (const dbInfo of databasesToCheck) {
			const dbName = dbInfo.name!;

			try {
				await checkDatabaseExpiry(dbName);
				// TODO: Add analytics for each database action
			} catch (error) {
				// TODO: Add analytics for individual database failure
			}
		}

		// TODO: Add analytics event for background cleanup summary
	} catch (error) {
		// TODO: Add analytics for complete background cleanup failure
	}
}
